import { GlassCard } from "@/components/ui/glass-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle, AlertCircle, Loader2, Terminal, ToggleLeft, ToggleRight } from "lucide-react";
import { type Device } from "@/hooks/useDevices";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DeviceAuditLogProps {
  device: Device;
}

const DeviceAuditLog = ({ device }: DeviceAuditLogProps) => {
  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      default:
        return <Terminal className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  const toggleRetry = async (deploymentId: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from('script_deployments')
        .update({ retry_enabled: !currentValue })
        .eq('id', deploymentId);

      if (error) throw error;

      toast.success(`Retry ${!currentValue ? 'enabled' : 'disabled'} for deployment`);
    } catch (error) {
      console.error('Error toggling retry:', error);
      toast.error('Failed to update retry setting');
    }
  };

  return (
    <GlassCard variant="default" hover={false} className="p-6">
      <h2 className="text-xl font-semibold mb-4 text-slate-900 dark:text-white">Audit Log</h2>
      <ScrollArea className="h-[300px]">
        <div className="space-y-4">
          {device.script_deployments && device.script_deployments.length > 0 ? (
            device.script_deployments.map((deployment) => (
              <div
                key={deployment.id}
                className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(deployment.status)}
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {deployment.configurations?.name || 'Unknown Script'}
                    </p>
                    <p className="text-sm text-slate-500">
                      Last run: {formatDate(deployment.last_run)}
                    </p>
                    {deployment.exit_code !== null && (
                      <p className="text-sm text-slate-500">
                        Exit code: {deployment.exit_code}
                      </p>
                    )}
                    {deployment.output && (
                      <p className="text-sm text-slate-500 mt-2 font-mono bg-slate-100 dark:bg-slate-800 p-2 rounded">
                        {deployment.output}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-sm px-3 py-1 rounded-full ${
                    deployment.status === 'success' ? 'bg-green-100 text-green-700' :
                    deployment.status === 'error' || deployment.status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {deployment.status}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2"
                    onClick={() => toggleRetry(deployment.id, deployment.retry_enabled || false)}
                  >
                    {deployment.retry_enabled ? (
                      <ToggleRight className="h-4 w-4" />
                    ) : (
                      <ToggleLeft className="h-4 w-4" />
                    )}
                    <span className="text-sm">
                      Retry {deployment.retry_enabled ? 'enabled' : 'disabled'}
                    </span>
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-slate-500">No actions recorded yet</p>
          )}
        </div>
      </ScrollArea>
    </GlassCard>
  );
};

export default DeviceAuditLog;